// Neon ribbon trails behind players (presentation only).
//
// This is NOT part of the deterministic sim and NOT networked. It records the
// world-space positions the renderer already computes for the local player,
// remote players and the ghost, then draws a camera-facing ribbon strip through
// them. The head (newest point) is bright and wide; the tail fades and narrows
// over the configured lifetime (Video -> Player trail, 0..3 s).
//
// Design notes:
//   - One shared VBO/IBO, rebuilt per trail (a handful of trails per frame).
//   - Points are appended at most once per ~6 ms and only while moving, so the
//     buffer size stays bounded regardless of render rate.
//   - A large position jump (respawn / teleport) starts a fresh trail instead of
//     drawing a line across the map.
//   - Additive blending + a hot white core makes it bloom with the existing
//     post-process; the ribbon is depth-tested so walls occlude it.

/** Trail colour mode chosen in the Video settings (see `settings.ts`). */

export interface TrailSample {
  /** Stable per-entity id (slot number, or "local"/"ghost"). */
  id: number | string;
  x: number;
  y: number;
  z: number;
  /** Linear-ish 0..1 RGB. */
  color: readonly [number, number, number];
}

interface TrailPoint {
  t: number;
  x: number;
  y: number;
  z: number;
}

interface Trail {
  points: TrailPoint[];
  color: [number, number, number];
  /** Last wall-clock time this trail received a sample. */
  seen: number;
}

/** Max retained points per trail (~5 s at 180 Hz). */
const MAX_POINTS = 900;
/** Minimum time between appended points (ms). */
const MIN_STEP_MS = 6;
/** A jump larger than this starts a fresh trail (respawn/teleport). */
const TELEPORT_DIST = 250;
/** Drop a trail that stopped being sampled (player left / ghost off). */
const STALE_MS = 250;
/** A sample closer than this just sticks to the previous head point. */
const STICK_DIST = 0.6;

const FLOATS_PER_VERT = 8; // pos.xyz, color.rgb, alpha, across
const INDICES_PER_SEGMENT = 6;

export class PlayerTrailSystem {
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private vbo: WebGLBuffer;
  private ibo: WebGLBuffer;
  private uProjView: WebGLUniformLocation | null;
  private verts = new Float32Array(MAX_POINTS * 2 * FLOATS_PER_VERT);
  private indices = new Uint32Array((MAX_POINTS - 1) * INDICES_PER_SEGMENT);
  private trails = new Map<number | string, Trail>();

  /** Trail lifetime in seconds (0 = off). */
  seconds = 1.0;
  /** Base ribbon half-width in world units. */
  width = 4.2;
  /** Overall brightness multiplier. */
  intensity = 1.0;
  /** Wall-clock time of the last `update` (used to fade points in `draw`). */
  private clock = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.program = this.link();
    this.uProjView = gl.getUniformLocation(this.program, "u_proj_view");

    this.vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.verts.byteLength, gl.DYNAMIC_DRAW);

    this.ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indices.byteLength, gl.DYNAMIC_DRAW);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    const stride = FLOATS_PER_VERT * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, stride, 24);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, stride, 28);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bindVertexArray(null);
  }

  private link(): WebGLProgram {
    const gl = this.gl;
    const vs = `#version 300 es
      layout(location=0) in vec3 a_pos;
      layout(location=1) in vec3 a_color;
      layout(location=2) in float a_alpha;
      layout(location=3) in float a_across;
      uniform mat4 u_proj_view;
      out vec3 v_color;
      out float v_alpha;
      out float v_across;
      void main() {
        gl_Position = u_proj_view * vec4(a_pos, 1.0);
        v_color = a_color;
        v_alpha = a_alpha;
        v_across = a_across;
      }`;
    const fs = `#version 300 es
      precision mediump float;
      in vec3 v_color;
      in float v_alpha;
      in float v_across;
      out vec4 outColor;
      void main() {
        float d = abs(v_across);
        // Saturated coloured core + softer halo; bloom supplies the white glow.
        float core = smoothstep(0.55, 0.0, d);
        float halo = smoothstep(1.0, 0.15, d);
        vec3 rgb = v_color * (0.55 + 1.05 * halo);
        float a = (core * 0.55 + halo * 0.30) * v_alpha;
        outColor = vec4(rgb, a);
      }`;
    const compile = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        throw new Error("trail shader: " + gl.getShaderInfoLog(s));
      }
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error("trail link: " + gl.getProgramInfoLog(p));
    }
    return p;
  }

  /** Drop every trail (map load / mode change). */
  clear(): void {
    this.trails.clear();
  }

  /** Drop one entity's trail (e.g. the ghost was turned off). */
  drop(id: number | string): void {
    this.trails.delete(id);
  }

  /** Debug/headless: per-trail point counts and head positions. */
  debugInfo(): {
    seconds: number;
    width: number;
    intensity: number;
    count: number;
    trails: { id: number | string; points: number; head: [number, number, number]; color: [number, number, number] }[];
  } {
    const out: {
      id: number | string;
      points: number;
      head: [number, number, number];
      color: [number, number, number];
    }[] = [];
    for (const [id, tr] of this.trails) {
      const p = tr.points[tr.points.length - 1];
      out.push({
        id,
        points: tr.points.length,
        head: p ? [p.x, p.y, p.z] : [0, 0, 0],
        color: [tr.color[0], tr.color[1], tr.color[2]],
      });
    }
    return { seconds: this.seconds, width: this.width, intensity: this.intensity, count: this.trails.size, trails: out };
  }

  /**
   * Record this frame's samples and age out old points. Call once per rendered
   * frame with every entity whose trail should be visible.
   */
  update(now: number, samples: readonly TrailSample[]): void {
    this.clock = now;
    if (this.seconds <= 0) {
      if (this.trails.size > 0) this.trails.clear();
      return;
    }
    const seen = new Set<number | string>();
    for (const s of samples) {
      seen.add(s.id);
      let tr = this.trails.get(s.id);
      if (!tr) {
        tr = { points: [], color: [s.color[0], s.color[1], s.color[2]], seen: now };
        this.trails.set(s.id, tr);
      }
      tr.color[0] = s.color[0];
      tr.color[1] = s.color[1];
      tr.color[2] = s.color[2];
      tr.seen = now;

      const pts = tr.points;
      const last = pts[pts.length - 1];
      if (last) {
        const dx = s.x - last.x;
        const dy = s.y - last.y;
        const dz = s.z - last.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        // Respawn/teleport: don't connect across the map.
        if (d2 > TELEPORT_DIST * TELEPORT_DIST) {
          pts.length = 0;
        } else if (now - last.t < MIN_STEP_MS) {
          // Too soon to add a point; keep the head glued to the player so the
          // trail doesn't visibly lag behind them.
          if (d2 < STICK_DIST * STICK_DIST) {
            last.x = s.x;
            last.y = s.y;
            last.z = s.z;
          }
          continue;
        } else if (d2 < STICK_DIST * STICK_DIST && now - last.t < 50) {
          last.x = s.x;
          last.y = s.y;
          last.z = s.z;
          continue;
        }
      }
      pts.push({ t: now, x: s.x, y: s.y, z: s.z });
      if (pts.length > MAX_POINTS) pts.shift();
    }

    // Age points + drop entities that stopped being sampled.
    const cutoff = now - this.seconds * 1000;
    for (const [id, tr] of this.trails) {
      if (now - tr.seen > STALE_MS) {
        this.trails.delete(id);
        continue;
      }
      const pts = tr.points;
      while (pts.length > 1 && pts[0].t < cutoff) pts.shift();
    }
  }

  /** Draw the ribbons. Assumes the world has filled the depth buffer. */
  draw(projView: Float32Array, eye: readonly [number, number, number]): void {
    if (this.seconds <= 0 || this.trails.size === 0) return;
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.uProjView, false, projView);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);

    for (const tr of this.trails.values()) {
      const n = tr.points.length;
      if (n < 2) continue;
      const ix = this.buildRibbon(tr, eye);
      if (ix === 0) continue;
      const vcount = n * 2;
      gl.bufferData(
        gl.ARRAY_BUFFER,
        this.verts.subarray(0, vcount * FLOATS_PER_VERT),
        gl.DYNAMIC_DRAW,
      );
      gl.bufferData(
        gl.ELEMENT_ARRAY_BUFFER,
        this.indices.subarray(0, ix),
        gl.DYNAMIC_DRAW,
      );
      gl.drawElements(gl.TRIANGLES, ix, gl.UNSIGNED_INT, 0);
    }

    gl.bindVertexArray(null);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  /**
   * Build a camera-facing ribbon from the trail's points into `verts`/`indices`.
   * Returns the index count (0 when the trail is degenerate).
   */
  private buildRibbon(tr: Trail, eye: readonly [number, number, number]): number {
    const pts = tr.points;
    const n = pts.length;
    const v = this.verts;
    const idx = this.indices;
    const now = this.clock;
    const span = Math.max(1e-3, this.seconds * 1000);
    const base = Math.max(0.2, this.width);
    let lastSide: [number, number, number] = [1, 0, 0];
    let icount = 0;

    for (let i = 0; i < n; i++) {
      const p = pts[i];
      // Path tangent (use one-sided differences at the ends).
      const a = i > 0 ? pts[i - 1] : p;
      const b = i < n - 1 ? pts[i + 1] : p;
      let tx = b.x - a.x;
      let ty = b.y - a.y;
      let tz = b.z - a.z;
      const tl = Math.hypot(tx, ty, tz);
      if (tl > 1e-4) {
        tx /= tl;
        ty /= tl;
        tz /= tl;
      } else {
        tx = 0;
        ty = 0;
        tz = 1;
      }
      // Camera-facing side = tangent x view direction.
      let ex = eye[0] - p.x;
      let ey = eye[1] - p.y;
      let ez = eye[2] - p.z;
      const el = Math.hypot(ex, ey, ez);
      if (el > 1e-4) {
        ex /= el;
        ey /= el;
        ez /= el;
      } else {
        ex = 1;
        ey = 0;
        ez = 0;
      }
      let sx = ty * ez - tz * ey;
      let sy = tz * ex - tx * ez;
      let sz = tx * ey - ty * ex;
      const sl = Math.hypot(sx, sy, sz);
      if (sl > 1e-4) {
        sx /= sl;
        sy /= sl;
        sz /= sl;
        lastSide = [sx, sy, sz];
      } else {
        [sx, sy, sz] = lastSide;
      }

      // 0 at the tail -> 1 at the head.
      const t = Math.min(1, Math.max(0, (p.t - (now - span)) / span));
      const k = Math.pow(t, 0.7);
      const w = base * (0.12 + 0.88 * k);
      const alpha = Math.pow(t, 0.85) * this.intensity;

      let o = i * 2 * FLOATS_PER_VERT;
      v[o] = p.x + sx * w;
      v[o + 1] = p.y + sy * w;
      v[o + 2] = p.z + sz * w;
      v[o + 3] = tr.color[0];
      v[o + 4] = tr.color[1];
      v[o + 5] = tr.color[2];
      v[o + 6] = alpha;
      v[o + 7] = 1;
      o += FLOATS_PER_VERT;
      v[o] = p.x - sx * w;
      v[o + 1] = p.y - sy * w;
      v[o + 2] = p.z - sz * w;
      v[o + 3] = tr.color[0];
      v[o + 4] = tr.color[1];
      v[o + 5] = tr.color[2];
      v[o + 6] = alpha;
      v[o + 7] = -1;

      if (i > 0) {
        const a0 = (i - 1) * 2;
        const b0 = a0 + 1;
        const c0 = i * 2;
        const d0 = c0 + 1;
        idx[icount++] = a0;
        idx[icount++] = b0;
        idx[icount++] = c0;
        idx[icount++] = b0;
        idx[icount++] = d0;
        idx[icount++] = c0;
      }
    }
    return icount;
  }
}
